'use client';

import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useState, type ReactNode } from 'react';

import { Menu } from 'lucide-react';

import KalshiLogo from '@/components/DesignSystem/Icons/adhoc/KalshiLogo';

import { cn } from '../lib/utils';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

/**
 * The site's sections. Graphics owns `/studio` and its template pages; every other section owns
 * its own path prefix.
 */
export const SITE_SECTIONS = [
    { href: '/studio', label: 'Graphics' },
    { href: '/studio/canvas', label: 'Canvas' },
    { href: '/studio/assets', label: 'Brand assets' },
    { href: '/widgets', label: 'Web widgets' },
] as const;

function activeSection(pathname: string | null): string | undefined {
    const owner = SITE_SECTIONS.slice(1).find((section) => pathname?.startsWith(section.href));
    if (owner) return owner.href;
    return pathname?.startsWith('/studio') ? '/studio' : undefined;
}

/** Studio sections carry the partner's code between pages; the widgets gallery has no use for it. */
function useSectionHref() {
    const code = useSearchParams().get('code');
    return (href: string) => (code && href.startsWith('/studio') ? `${href}?code=${encodeURIComponent(code)}` : href);
}

/** kalshi.com's navbar logo (green, 26px), with the site's name in its nav-link capitals. */
function Brand() {
    return (
        <span className="flex items-center gap-3">
            <KalshiLogo color="var(--brand-primary)" height="26px" />
            <span className="font-kalshi-sans text-[13px] font-semibold uppercase leading-[18px] tracking-[0.08em] text-text-x20">
                Studio
            </span>
        </span>
    );
}

function SectionLinks({ onNavigate, stacked }: { onNavigate?: () => void; stacked?: boolean }) {
    const pathname = usePathname();
    const active = activeSection(pathname);
    const hrefFor = useSectionHref();
    return (
        <>
            {SITE_SECTIONS.map((section) => (
                <Link
                    key={section.href}
                    href={hrefFor(section.href)}
                    onClick={onNavigate}
                    aria-current={active === section.href ? 'page' : undefined}
                    // kalshi.com's nav links: tracked capitals on a pill hover, the current one green.
                    className={cn(
                        'inline-flex min-h-9 items-center rounded-x50 px-4 font-kalshi-sans text-[13px] font-semibold uppercase leading-[18px] tracking-[0.08em] transition-colors hover:bg-fill-x60',
                        stacked && 'flex',
                        active === section.href ? 'text-green-x10' : 'text-text-x10',
                    )}
                >
                    {section.label}
                </Link>
            ))}
        </>
    );
}

/**
 * One header for the whole site. On phones the sections move into a sheet, where `menuExtra`
 * (the widgets gallery's categories) follows them.
 */
export function SiteHeader({ menuExtra }: { menuExtra?: ReactNode }) {
    const [open, setOpen] = useState(false);
    const hrefFor = useSectionHref();
    return (
        <header className="sticky top-0 z-30 border-b border-solid border-fill-x50 bg-surface-x10/85 backdrop-blur supports-[backdrop-filter]:bg-surface-x10/70">
            <div className="flex h-16 items-center gap-6 px-6 wsm:px-8">
                <Link
                    href={hrefFor('/studio')}
                    className="transition-opacity hover:opacity-70"
                    aria-label="Kalshi Studio home"
                >
                    <Brand />
                </Link>
                <nav aria-label="Sections" className="ml-auto hidden items-center gap-1 wsm:flex">
                    <SectionLinks />
                </nav>
                <div className="ml-auto wsm:hidden">
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger
                            aria-label="Open navigation"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-x30 border border-stroke-x30 text-text-x10"
                        >
                            <Menu className="h-4 w-4" aria-hidden="true" />
                        </SheetTrigger>
                        <SheetContent side="left" className="flex flex-col gap-6 overflow-y-auto bg-surface-x10">
                            <Brand />
                            <nav aria-label="Sections" className="flex flex-col gap-0.5">
                                <SectionLinks stacked onNavigate={() => setOpen(false)} />
                            </nav>
                            {menuExtra ? <div onClick={() => setOpen(false)}>{menuExtra}</div> : null}
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}
